import { useState } from "react";
import { useAppDispatch } from "../app/hooks";
import { useTranslation } from "react-i18next";
import { insertVertex, deleteVertex } from "./patternSlice";
import { Vertex } from "./points";

const HANDLE_RADIUS = 8;

interface HandleProps {
  vertices: Vertex[];
  index: number;
  onMouseDown: Function;
  onClick: Function;
}

function Handle({ vertices, index, onMouseDown, onClick }: HandleProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [active, setActive] = useState(false);
  const [initialClick, setInitialClick] = useState({ x: 0, y: 0 });

  const vertex = vertices[index];
  const { x, y, type } = vertex;

  return (
    <g
      className="handle"
      onClick={(e) => {
        if (vertex.type === "control") {
          return;
        }

        if (e.clientX === initialClick.x && e.clientY === initialClick.y) {
          setActive(true);
        }
      }}
    >
      {vertex.type === "control" && (
        <line
          className="curve-visual-helper"
          stroke="orange"
          x1={x}
          y1={y}
          x2={vertices[index + 1].x}
          y2={vertices[index + 1].y}
        />
      )}
      <circle
        cx={x}
        cy={y}
        className={`${index ? type : "start"}`}
        r={HANDLE_RADIUS}
        onMouseDown={(e) => {
          onMouseDown(index);
          setInitialClick({ x: e.clientX, y: e.clientY });
        }}
        onClick={(e) => {
          if (
            active &&
            (e.clientX === initialClick.x || e.clientY === initialClick.y)
          ) {
            e.stopPropagation();
            setActive(false);
          }
        }}
      />
      <text x={x - HANDLE_RADIUS / 2} y={y + HANDLE_RADIUS / 2}>
        {index}
      </text>

      {active && (
        <g>
          {index && vertices[index - 1].type !== "control" && (
            <g
              className="curve-button"
              onClick={(e) => {
                e.stopPropagation();

                let newVertex: Vertex = { ...vertex };

                newVertex.type = "control";
                const DELTA = 50;
                newVertex.x = vertex.x + DELTA;
                newVertex.y = vertex.y + DELTA;

                setActive(false);
                dispatch(insertVertex({ index, vertex: newVertex }));
              }}
            >
              <title>{t("change to curve")}</title>
              <circle
                cx={x - HANDLE_RADIUS * 3}
                cy={y - HANDLE_RADIUS * 2}
                className={`${index ? type : "start"}`}
                r={HANDLE_RADIUS}
              />
              <text
                x={x - HANDLE_RADIUS * 3 - HANDLE_RADIUS / 2}
                y={y - HANDLE_RADIUS * 2 + HANDLE_RADIUS / 2}
              >
                ɿ
              </text>
            </g>
          )}

          <g
            className="delete-button"
            onClick={(e) => {
              e.stopPropagation();
              setActive(false);
              dispatch(deleteVertex(index));
            }}
          >
            <title>{t("delete vexter")}</title>
            <circle
              cx={x - HANDLE_RADIUS * 3}
              cy={y + HANDLE_RADIUS * 1}
              className={`${index ? type : "start"}`}
              r={HANDLE_RADIUS}
            />
            <text
              x={x - HANDLE_RADIUS * 3 - HANDLE_RADIUS / 2}
              y={y + HANDLE_RADIUS * 1 + HANDLE_RADIUS / 2}
            >
              X
            </text>
          </g>
        </g>
      )}
    </g>
  );
}

interface HandlesProps {
  vertices: Vertex[];
  onMouseDown: Function;
  onClick: Function;
}

export default function Handles({
  vertices,
  onMouseDown,
  onClick,
}: HandlesProps) {
  return (
    <>
      {vertices.map((vertex, i) => (
        <Handle
          key={i}
          vertices={vertices}
          index={i}
          onMouseDown={onMouseDown}
          onClick={onClick}
        />
      ))}
    </>
  );
}
